import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

// Layout Components
import Navbar from './components/Layout/Navbar';
import HomePage from './pages/HomePage';

// Auth Components
import Login from './components/Auth/Login';
import StudentLogin from './components/Auth/StudentLogin';
import FacultyLogin from './components/Auth/FacultyLogin';
import AdminLogin from './components/Auth/AdminLogin';
import AuthorityLogin from './components/Auth/AuthorityLogin';
import StudentRegister from './components/Auth/StudentRegister';
import FacultyRegister from './components/Auth/FacultyRegister';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';

// Complaint Components
import CreateComplaint from './components/Complaints/CreateComplaint';
import ComplaintList from './components/Complaints/ComplaintList';
import ComplaintDetail from './components/Complaints/ComplaintDetail';

// Dashboard Components
import Dashboard from './components/Dashboard/Dashboard';

// Authority Components
import AuthorityDashboard from './components/Authority/AuthorityDashboard';
import CreateAdmin from './components/Authority/CreateAdmin';
import ManageAdmins from './components/Authority/ManageAdmins';
import AssignedComplaints from './components/Authority/AssignedComplaints';
import ReportsDashboard from './components/Authority/Reports/ReportsDashboard';
import ResolveComplaint from './components/Authority/ResolveComplaint';
import AuthorityComplaintsHistory from './components/Authority/AuthorityComplaintsHistory';
import UsersList from './components/Authority/UsersList';
import AuthorityReport from './components/Authority/AuthorityReport';

// Higher Authority Components
import HigherAuthorityDashboard from './components/HigherAuthority/HigherAuthorityDashboard';
import HACreateAdmin from './components/HigherAuthority/CreateAdmin';
import HACreateAuthority from './components/HigherAuthority/CreateAuthority';
import HAManageAdmins from './components/HigherAuthority/ManageAdmins';
import HAManageAuthorities from './components/HigherAuthority/ManageAuthorities';
import HAAdminReports from './components/HigherAuthority/AdminReports';
import HAAuthorityReports from './components/HigherAuthority/AuthorityReports';
import HAAllUsers from './components/HigherAuthority/AllUsers';
import HAPerformance from './components/HigherAuthority/PerformanceAnalytics';
import HAEscalatedComplaints from './components/HigherAuthority/EscalatedComplaints';

// Admin Components
import AdminDashboard from './components/Admin/AdminDashboard';
import PendingComplaints from './components/Admin/PendingComplaints';
import VerifiedComplaints from './components/Admin/VerifiedComplaints';
import VerifyComplaint from './components/Admin/VerifyComplaint';
import AdminReport from './components/Admin/AdminReport';
import AdminComplaintsHistory from './components/Admin/AdminComplaintsHistory';

// Profile Component
import ProfilePage from './components/Profile/ProfilePage';

// Community Component
import CommunityPage from './components/Community/CommunityPage';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/" />;
}

function RoleBasedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" />;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/dashboard" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return !user ? children : <Navigate to="/dashboard" />;
}

function DashboardRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" />;
  if (user.role === 'higher_authority') return <HigherAuthorityDashboard />;
  if (user.role === 'authority') return <AuthorityDashboard />;
  if (user.role === 'admin' || user.role === 'super_admin') return <AdminDashboard />;
  return <Dashboard />;
}

function LayoutWrapper({ children }) {
  const location = useLocation();
  const hideNavbarPaths = [
    '/login', '/login/student', '/login/faculty',
    '/admin-login', '/authority-login',
    '/register/student', '/register/faculty',
    '/forgot-password', '/reset-password',
  ];
  return (
    <>
      {!hideNavbarPaths.includes(location.pathname) && <Navbar />}
      {children}
    </>
  );
}

function App() {
  const rootBg = '#0d0d14';

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    localStorage.setItem('ur-theme', 'dark');
  }, []);

  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <div style={{ minHeight: '100vh', background: rootBg, overflowX: 'hidden' }}>
            <LayoutWrapper>
              <main style={{ paddingBottom: 40, overflowX: 'hidden' }}>
                <Routes>
                  {/* Public */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login"            element={<PublicRoute><Login /></PublicRoute>} />
                  <Route path="/login/student"    element={<PublicRoute><StudentLogin /></PublicRoute>} />
                  <Route path="/login/faculty"    element={<PublicRoute><FacultyLogin /></PublicRoute>} />
                  <Route path="/admin-login"      element={<PublicRoute><AdminLogin /></PublicRoute>} />
                  <Route path="/authority-login"  element={<PublicRoute><AuthorityLogin /></PublicRoute>} />
                  <Route path="/register/student" element={<PublicRoute><StudentRegister /></PublicRoute>} />
                  <Route path="/register/faculty" element={<PublicRoute><FacultyRegister /></PublicRoute>} />
                  <Route path="/forgot-password"  element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                  <Route path="/reset-password"   element={<PublicRoute><ResetPassword /></PublicRoute>} />

                  {/* Dashboard */}
                  <Route path="/dashboard"        element={<PrivateRoute><DashboardRouter /></PrivateRoute>} />
                  <Route path="/complaints"        element={<PrivateRoute><ComplaintList /></PrivateRoute>} />
                  <Route path="/complaints/new"    element={<PrivateRoute><CreateComplaint /></PrivateRoute>} />
                  <Route path="/complaints/:id"    element={<PrivateRoute><ComplaintDetail /></PrivateRoute>} />
                  <Route path="/profile"           element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                  <Route path="/community"         element={<PrivateRoute><CommunityPage /></PrivateRoute>} />

                  {/* Authority */}
                  <Route path="/authority/dashboard"
                    element={<RoleBasedRoute allowedRoles={['authority']}><AuthorityDashboard /></RoleBasedRoute>} />
                  <Route path="/authority/create-admin"
                    element={<RoleBasedRoute allowedRoles={['authority']}><CreateAdmin /></RoleBasedRoute>} />
                  <Route path="/authority/admins"
                    element={<RoleBasedRoute allowedRoles={['authority']}><ManageAdmins /></RoleBasedRoute>} />
                  <Route path="/authority/assigned"
                    element={<RoleBasedRoute allowedRoles={['authority']}><AssignedComplaints /></RoleBasedRoute>} />
                  <Route path="/authority/reports"
                    element={<RoleBasedRoute allowedRoles={['authority']}><ReportsDashboard /></RoleBasedRoute>} />
                  <Route path="/authority/resolve/:id"
                    element={<RoleBasedRoute allowedRoles={['authority']}><ResolveComplaint /></RoleBasedRoute>} />
                  <Route path="/authority/complaints-history"
                    element={<RoleBasedRoute allowedRoles={['authority']}><AuthorityComplaintsHistory /></RoleBasedRoute>} />
                  <Route path="/authority/users"
                    element={<RoleBasedRoute allowedRoles={['authority']}><UsersList /></RoleBasedRoute>} />
                  <Route path="/authority/report"
                    element={<RoleBasedRoute allowedRoles={['authority']}><AuthorityReport /></RoleBasedRoute>} />

                  {/* Higher Authority */}
                  <Route path="/higher-authority/dashboard"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HigherAuthorityDashboard /></RoleBasedRoute>} />
                  <Route path="/higher-authority/create-admin"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HACreateAdmin /></RoleBasedRoute>} />
                  <Route path="/higher-authority/create-authority"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HACreateAuthority /></RoleBasedRoute>} />
                  <Route path="/higher-authority/admins"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAManageAdmins /></RoleBasedRoute>} />
                  <Route path="/higher-authority/authorities"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAManageAuthorities /></RoleBasedRoute>} />
                  <Route path="/higher-authority/admin-reports"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAAdminReports /></RoleBasedRoute>} />
                  <Route path="/higher-authority/authority-reports"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAAuthorityReports /></RoleBasedRoute>} />
                  <Route path="/higher-authority/users"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAAllUsers /></RoleBasedRoute>} />
                  <Route path="/higher-authority/performance"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAPerformance /></RoleBasedRoute>} />
                  <Route path="/higher-authority/escalated"
                    element={<RoleBasedRoute allowedRoles={['higher_authority']}><HAEscalatedComplaints /></RoleBasedRoute>} />

                  {/* Admin */}
                  <Route path="/admin/dashboard"
                    element={<RoleBasedRoute allowedRoles={['admin','super_admin']}><AdminDashboard /></RoleBasedRoute>} />
                  <Route path="/admin/pending-complaints"
                    element={<RoleBasedRoute allowedRoles={['admin','super_admin']}><PendingComplaints /></RoleBasedRoute>} />
                  <Route path="/admin/verified-complaints"
                    element={<RoleBasedRoute allowedRoles={['admin','super_admin']}><VerifiedComplaints /></RoleBasedRoute>} />
                  <Route path="/admin/verify/:id"
                    element={<RoleBasedRoute allowedRoles={['admin','super_admin']}><VerifyComplaint /></RoleBasedRoute>} />
                  <Route path="/admin/report"
                    element={<RoleBasedRoute allowedRoles={['admin','super_admin']}><AdminReport /></RoleBasedRoute>} />
                  <Route path="/admin/complaints-history"
                    element={<RoleBasedRoute allowedRoles={['admin','super_admin']}><AdminComplaintsHistory /></RoleBasedRoute>} />

                  {/* 404 */}
                  <Route path="*" element={
                    <div style={{ textAlign: 'center', marginTop: 100 }}>
                      <h2>404 - Page Not Found</h2>
                      <button onClick={() => window.location.href = '/'} style={{ marginTop: 20, padding: '10px 20px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 16 }}>
                        Go to Home
                      </button>
                    </div>
                  } />
                </Routes>
              </main>
            </LayoutWrapper>
          </div>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
